import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  Users, 
  BookOpen, 
  TrendingUp, 
  Activity,
  ArrowRight,
  BarChart3,
  Mail,
  Send,
  Clock,
  AlertCircle,
  Unlock,
  Bell,
  CheckCircle,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

import { API } from "../../utils/api";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usersNeedingReminder, setUsersNeedingReminder] = useState([]);
  const [pendingUnlocks, setPendingUnlocks] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingUnlockNotifications, setSendingUnlockNotifications] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchUsersNeedingReminder();
    fetchPendingUnlocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAnalytics = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/analytics`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersNeedingReminder = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/users-needing-reminder`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUsersNeedingReminder(data);
      }
    } catch (error) {
      console.error("Error fetching users needing reminder:", error);
    }
  };

  const fetchPendingUnlocks = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/pending-unlock-notifications`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPendingUnlocks(data);
      }
    } catch (error) {
      console.error("Error fetching pending unlocks:", error);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/send-reminders`, {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchUsersNeedingReminder();
      } else {
        toast.error("Erreur lors de l'envoi des rappels");
      }
    } catch (error) {
      console.error("Error sending reminders:", error);
      toast.error("Erreur lors de l'envoi des rappels");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendUnlockNotifications = async () => {
    setSendingUnlockNotifications(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/check-and-send-unlock-notifications`, {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPendingUnlocks();
      } else {
        toast.error("Erreur lors de l'envoi des notifications");
      }
    } catch (error) {
      console.error("Error sending unlock notifications:", error);
      toast.error("Erreur lors de l'envoi des notifications");
    } finally {
      setSendingUnlockNotifications(false);
    }
  };

  const pcmColors = {
    Analyseur: "#32A2FF",
    Persévérant: "#7F379D",
    Empathique: "#FC7403",
    Énergiseur: "#FAE200",
    Promoteur: "#FF1933",
    Imagineur: "#E3A88D",
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D] mb-2">
            Administration
          </h1>
          <p className="text-slate-600">Vue d'ensemble de la plateforme</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card 
            className="bg-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/admin/users")}
            data-testid="total-users-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Utilisateurs</p>
                  <p className="text-3xl font-bold text-[#101E4D]">
                    {analytics?.users?.total || analytics?.total_users || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {analytics?.users?.active || analytics?.active_users || 0} actifs
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#101E4D]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#101E4D]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/admin/coaching")}
            data-testid="engagement-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Engagement</p>
                  <p className="text-3xl font-bold text-[#FF6B35]">
                    {analytics?.engagement?.total_answers || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    réponses enregistrées
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-[#FF6B35]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/admin/coaching")}
            data-testid="questions-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Questions</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {analytics?.engagement?.pending_questions || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    en attente de réponse
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white" data-testid="completion-rate-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Programme terminé</p>
                  <p className="text-3xl font-bold text-green-600">
                    {analytics?.users?.completed_program || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    coachés ont terminé
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 border">
            <p className="text-xs text-slate-500 mb-1">En attente</p>
            <p className="text-xl font-semibold text-yellow-600">{analytics?.users?.pending || 0}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border">
            <p className="text-xs text-slate-500 mb-1">Onboardés</p>
            <p className="text-xl font-semibold text-blue-600">{analytics?.users?.onboarded || 0}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border">
            <p className="text-xs text-slate-500 mb-1">Activité 7j</p>
            <p className="text-xl font-semibold text-orange-600">{analytics?.engagement?.recent_answers_7d || 0}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border">
            <p className="text-xs text-slate-500 mb-1">Notes coach</p>
            <p className="text-xl font-semibold text-purple-600">{analytics?.engagement?.coach_notes || 0}</p>
          </div>
        </div>

        {/* Top Engaged Users & PCM Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Engaged Users */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <TrendingUp className="w-5 h-5 mr-2 text-[#FF6B35]" />
                Top coachés engagés
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.top_engaged_users?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_engaged_users.slice(0, 5).map((user, index) => (
                    <div key={user._id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium text-slate-700">{user.name}</span>
                      </div>
                      <span className="text-sm text-slate-500">{user.answer_count} réponses</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">Aucune donnée</p>
              )}
            </CardContent>
          </Card>

          {/* PCM Distribution */}
          <Card className="bg-white" data-testid="pcm-base-distribution">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <BarChart3 className="w-5 h-5 mr-2 text-[#FF6B35]" />
                Distribution des Bases PCM
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics?.pcm_distribution?.base?.length > 0 || analytics?.pcm_base_distribution?.length > 0) ? (
                <div className="space-y-4">
                  {(analytics.pcm_distribution?.base || analytics.pcm_base_distribution || []).map((item) => (
                    <div key={item._id} className="flex items-center">
                      <div className="w-28 text-sm font-medium text-slate-700">
                        {item._id}
                      </div>
                      <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
                        <div
                          className="h-full rounded-lg transition-all duration-500"
                          style={{
                            width: `${Math.max(
                              (item.count / (analytics?.users?.total || analytics?.total_users || 1)) * 100,
                              5
                            )}%`,
                            backgroundColor: pcmColors[item._id] || "#64748B",
                          }}
                        />
                      </div>
                      <div className="w-12 text-right text-sm font-semibold text-slate-700">
                        {item.count}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white" data-testid="pcm-phase-distribution">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <BarChart3 className="w-5 h-5 mr-2 text-[#FF6B35]" />
                Distribution des Phases PCM
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics?.pcm_distribution?.phase?.length > 0 || analytics?.pcm_phase_distribution?.length > 0) ? (
                <div className="space-y-4">
                  {(analytics.pcm_distribution?.phase || analytics.pcm_phase_distribution || []).map((item) => (
                    <div key={item._id} className="flex items-center">
                      <div className="w-28 text-sm font-medium text-slate-700">
                        {item._id}
                      </div>
                      <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
                        <div
                          className="h-full rounded-lg transition-all duration-500"
                          style={{
                            width: `${Math.max(
                              (item.count / (analytics?.users?.total || analytics?.total_users || 1)) * 100,
                              5
                            )}%`,
                            backgroundColor: pcmColors[item._id] || "#64748B",
                          }}
                        />
                      </div>
                      <div className="w-12 text-right text-sm font-semibold text-slate-700">
                        {item.count}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Email Reminders Section */}
        <Card className="bg-white mb-8" data-testid="email-reminders-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-[#101E4D]">
              <div className="flex items-center">
                <Mail className="w-5 h-5 mr-2 text-[#FF6B35]" />
                Rappels par email
              </div>
              <Button
                onClick={handleSendReminders}
                disabled={sendingReminders || usersNeedingReminder.length === 0}
                className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                data-testid="send-reminders-btn"
              >
                {sendingReminders ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer les rappels
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usersNeedingReminder.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 mb-4">
                  {usersNeedingReminder.length} utilisateur(s) inactif(s) depuis plus de 3 jours
                </p>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {usersNeedingReminder.map((item) => (
                    <div
                      key={item.user?.user_id || item.user?.email}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#101E4D] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {item.user?.name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#101E4D]">{item.user?.name}</p>
                          <p className="text-xs text-slate-500">{item.user?.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-[#FF6B35] font-medium">Module {item.current_module}</p>
                        <p className="text-xs text-slate-400 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {item.status === "not_started" ? "Jamais commencé" : 
                            item.last_activity ? `Dernière activité: ${new Date(item.last_activity).toLocaleDateString('fr-FR')}` : "Inactif"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Tous les utilisateurs sont actifs !</p>
                <p className="text-sm">Aucun rappel à envoyer</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Module Unlock Notifications Section */}
        <Card className="bg-white mb-8" data-testid="unlock-notifications-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-[#101E4D]">
              <div className="flex items-center">
                <Unlock className="w-5 h-5 mr-2 text-green-500" />
                Notifications de déblocage
              </div>
              <Button
                onClick={handleSendUnlockNotifications}
                disabled={sendingUnlockNotifications || pendingUnlocks.filter(p => p.status === "unlocked" && !p.notification_sent).length === 0}
                className="bg-green-500 hover:bg-green-600 text-white"
                data-testid="send-unlock-notifications-btn"
              >
                {sendingUnlockNotifications ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Envoyer les notifications
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingUnlocks.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 mb-4">
                  {pendingUnlocks.filter(p => p.status === "unlocked" && !p.notification_sent).length} module(s) débloqué(s) en attente de notification
                </p>
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {pendingUnlocks.map((item) => (
                    <div
                      key={`${item.user_id}-${item.module_id}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        item.status === "unlocked" 
                          ? item.notification_sent 
                            ? "bg-green-50 border border-green-200" 
                            : "bg-orange-50 border border-orange-200"
                          : "bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          item.status === "unlocked" ? "bg-green-500" : "bg-slate-300"
                        }`}>
                          {item.status === "unlocked" ? (
                            <Unlock className="w-5 h-5 text-white" />
                          ) : (
                            <Clock className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#101E4D]">{item.user?.name}</p>
                          <p className="text-xs text-slate-500">{item.user?.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${item.status === "unlocked" ? "text-green-600" : "text-slate-500"}`}>
                          Module {item.module_number}
                        </p>
                        <p className="text-xs text-slate-400 flex items-center justify-end">
                          {item.status === "unlocked" ? (
                            item.notification_sent ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                                Notifié
                              </>
                            ) : (
                              <>
                                <Bell className="w-3 h-3 mr-1 text-orange-500" />
                                À notifier
                              </>
                            )
                          ) : (
                            <>
                              <Calendar className="w-3 h-3 mr-1" />
                              Dans {item.days_until_unlock}j
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Unlock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Aucun déblocage en attente</p>
                <p className="text-sm">Les notifications seront envoyées automatiquement</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-white" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="text-[#101E4D]">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate("/admin/users")}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                data-testid="quick-action-users"
              >
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-[#101E4D] mr-3" />
                  <span className="font-medium text-[#101E4D]">Gérer les utilisateurs</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#FF6B35] group-hover:translate-x-1 transition-all" />
              </button>

              <button
                onClick={() => navigate("/admin/modules")}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                data-testid="quick-action-modules"
              >
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 text-[#101E4D] mr-3" />
                  <span className="font-medium text-[#101E4D]">Gérer les modules</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#FF6B35] group-hover:translate-x-1 transition-all" />
              </button>

              <button
                onClick={() => navigate("/admin/users")}
                className="flex items-center justify-between p-4 bg-[#FF6B35]/10 rounded-xl hover:bg-[#FF6B35]/20 transition-colors group"
                data-testid="quick-action-create-user"
              >
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-[#FF6B35] mr-3" />
                  <span className="font-medium text-[#FF6B35]">Créer un utilisateur</span>
                </div>
                <ArrowRight className="w-4 h-4 text-[#FF6B35] group-hover:translate-x-1 transition-all" />
              </button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
